import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ContentContainer from "../../../templates/content-container/contentContainer";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import { PAGE_IDS } from "../../../app/data/common/pageIds";
import { userSelector } from "../../../slices/user/userSlice";
import { useSelector } from "react-redux";
import { UserUtils } from "../../../app/data/user/userUtils";
import PaymentService from "../../../app/data/payment/paymentService";
import {
  PaymentAggregates,
  PaymentDetail,
  PaymentHealth,
  PaymentSummary,
} from "../../../app/data/payment/PaymentSession";
import PaymentDetailSidebar from "./paymentDetailSidebar";
import PaymentSearchSection from "./paymentSearchSection";
import PaymentReportSection from "./paymentReportSection";
import PaymentHealthSection from "./paymentHealthSection";
import "./paymentAdminBrowser.scss";

const paymentService = PaymentService.getInstance();

const PaymentAdminBrowser: React.FC = () => {
  const userState = useSelector(userSelector);
  const location = useLocation();
  const isAdmin = UserUtils.canAccessFinancePayments(userState.profile);
  const initialInvoiceFromQuery = (() => {
    const raw = new URLSearchParams(location.search).get("invoiceNumber");
    return raw && /^\d+$/.test(raw) ? raw : "";
  })();
  const [tab, setTab] = useState<"browser" | "report" | "health">("browser");
  const [rows, setRows] = useState<PaymentSummary[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [status, setStatus] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState(initialInvoiceFromQuery);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);
  const [detail, setDetail] = useState<PaymentDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [reportAccount, setReportAccount] = useState("");
  const [reportProvider, setReportProvider] = useState("");
  const [reportFrom, setReportFrom] = useState("");
  const [reportTo, setReportTo] = useState("");
  const [aggregates, setAggregates] = useState<PaymentAggregates | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const [healthHours, setHealthHours] = useState(24);
  const [health, setHealth] = useState<PaymentHealth | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await paymentService.adminSearchPayments({
      accountNumber: accountNumber ? Number(accountNumber) : undefined,
      invoiceNumber: invoiceNumber ? Number(invoiceNumber) : undefined,
      status: status || undefined,
      page,
      size: 50,
    });
    if (res.ok() && res.data) {
      setRows(res.data.content || []);
      setTotalPages(res.data.totalPages || 0);
    } else {
      setError(
        typeof res.getError?.() === "string" ? (res.getError() as string) : "Failed to search payments"
      );
      setRows([]);
    }
    setLoading(false);
  }, [accountNumber, invoiceNumber, page, status]);

  const loadReport = useCallback(async () => {
    setReportLoading(true);
    setReportError(null);
    const res = await paymentService.adminAggregates({
      accountNumber: reportAccount ? Number(reportAccount) : undefined,
      provider: reportProvider || undefined,
      dateFrom: reportFrom || undefined,
      dateTo: reportTo || undefined,
    });
    if (res.ok() && res.data) {
      setAggregates(res.data);
    } else {
      setAggregates(null);
      setReportError(
        typeof res.getError?.() === "string" ? (res.getError() as string) : "Failed to load payment report"
      );
    }
    setReportLoading(false);
  }, [reportAccount, reportFrom, reportProvider, reportTo]);

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    setHealthError(null);
    const res = await paymentService.adminHealth(healthHours);
    if (res.ok() && res.data) {
      setHealth(res.data);
    } else {
      setHealth(null);
      setHealthError(
        typeof res.getError?.() === "string" ? (res.getError() as string) : "Failed to load payment health"
      );
    }
    setHealthLoading(false);
  }, [healthHours]);

  useEffect(() => {
    if (isAdmin && tab === "browser") load();
  }, [isAdmin, load, tab]);

  useEffect(() => {
    if (isAdmin && tab === "report") loadReport();
  }, [isAdmin, loadReport, tab]);

  useEffect(() => {
    if (isAdmin && tab === "health") loadHealth();
  }, [isAdmin, loadHealth, tab]);

  const closeDetail = () => {
    setSelectedPaymentId(null);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(false);
  };

  const openDetail = async (paymentId: string) => {
    setSelectedPaymentId(paymentId);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    const res = await paymentService.getPaymentDetail(paymentId);
    if (res.ok() && res.data) {
      setDetail(res.data);
    } else {
      setDetailError(
        typeof res.getError?.() === "string" ? (res.getError() as string) : "Failed to load detail"
      );
    }
    setDetailLoading(false);
  };

  const exportReportCsv = () => {
    if (!aggregates) return;
    const lines = [
      "metric,value",
      `totalPayments,${aggregates.totalPayments}`,
      `completedCount,${aggregates.completedCount}`,
      `failedCount,${aggregates.failedCount}`,
      `pendingCount,${aggregates.pendingCount}`,
      `cancelledCount,${aggregates.cancelledCount}`,
      `expiredCount,${aggregates.expiredCount}`,
      `completedAmountCents,${aggregates.completedAmountCents}`,
      `completedSurchargeCents,${aggregates.completedSurchargeCents ?? 0}`,
      `completedTotalChargedCents,${aggregates.completedTotalChargedCents ?? aggregates.completedAmountCents}`,
      `successRate,${aggregates.successRate}`,
      `failRate,${aggregates.failRate}`,
      ...Object.entries(aggregates.countByProvider || {}).map(
        ([k, v]) => `providerCount_${k},${v}`
      ),
      ...(aggregates.completedAmountByProvider || []).flatMap((p) => [
        `completedAmount_${p.provider},${p.amountCents}`,
        `completedSurcharge_${p.provider},${p.surchargeCents ?? 0}`,
        `completedTotalCharged_${p.provider},${p.totalChargedCents ?? p.amountCents}`,
      ]),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payment-report-${reportFrom || "all"}-${reportTo || "all"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isAdmin) {
    return (
      <ContentContainer title="Payments">
        <XGSErrorMessage>Only XGS administrators and Finance Admins can view the payment browser.</XGSErrorMessage>
      </ContentContainer>
    );
  }

  return (
    <ContentContainer title="Payment browser" isLoading={loading || reportLoading || healthLoading}>
      <div className="xgs-payment-admin">
        <div className="xgs-payment-admin__tabs">
          <Button
            theme={tab === "browser" ? ButtonThemes.blue : ButtonThemes.gray}
            onClick={() => setTab("browser")}
          >
            Search
          </Button>
          <Button
            theme={tab === "report" ? ButtonThemes.blue : ButtonThemes.gray}
            onClick={() => setTab("report")}
          >
            Report
          </Button>
          <Button
            theme={tab === "health" ? ButtonThemes.blue : ButtonThemes.gray}
            onClick={() => setTab("health")}
          >
            Health
          </Button>
        </div>

        {tab === "browser" && (
          <PaymentSearchSection
            accountNumber={accountNumber}
            invoiceNumber={invoiceNumber}
            status={status}
            page={page}
            totalPages={totalPages}
            rows={rows}
            error={error}
            onAccountChange={(value) => {
              setPage(0);
              setAccountNumber(value);
            }}
            onInvoiceChange={(value) => {
              setPage(0);
              setInvoiceNumber(value);
            }}
            onStatusChange={(value) => {
              setPage(0);
              setStatus(value);
            }}
            onSearch={() => load()}
            onPageChange={setPage}
            onRowClick={openDetail}
          />
        )}

        {tab === "report" && (
          <PaymentReportSection
            reportAccount={reportAccount}
            reportProvider={reportProvider}
            reportFrom={reportFrom}
            reportTo={reportTo}
            aggregates={aggregates}
            reportError={reportError}
            isLoading={reportLoading}
            onAccountChange={setReportAccount}
            onProviderChange={setReportProvider}
            onFromChange={setReportFrom}
            onToChange={setReportTo}
            onRunReport={() => loadReport()}
            onExportCsv={exportReportCsv}
          />
        )}

        {tab === "health" && (
          <PaymentHealthSection
            healthHours={healthHours}
            health={health}
            healthError={healthError}
            isLoading={healthLoading}
            onHoursChange={setHealthHours}
            onRefresh={() => loadHealth()}
            onFailureClick={openDetail}
          />
        )}

        <PaymentDetailSidebar
          show={!!selectedPaymentId || !!detailError}
          isLoading={detailLoading}
          detail={detail}
          detailError={detailError}
          onClose={closeDetail}
        />

        <span className="xgs-payment-admin__page-id" data-page-id={PAGE_IDS.PAYMENT_ADMIN} />
      </div>
    </ContentContainer>
  );
};

export default PaymentAdminBrowser;
